import { updateStreak } from '@/lib/streak';
import type { StreakState } from '@/lib/streak';
import { getOrCreateClientId as getOrCreateClientIdFromLib } from '@/lib/client-id';
import type { Zone } from '@/lib/zones';
import { SCHEMA_VERSION, SHOTS_PER_DAY } from '@/lib/constants';

export type Outcome = 'goal' | 'saved' | 'off';

export type ShotResult = {
  targetZone: Zone;
  actualZone: Zone | 'off';
  keeperZone: Zone;
  outcome: Outcome;
};

// LocalState.schemaVersion stays at 1 — that's the LocalStorage WRAPPER format.
// DailyRecord.schemaVersion is the per-day record format, bumped to 2 in Plan #5.
export type DailyRecord = {
  schemaVersion: 1 | 2;
  shots: ShotResult[];   // v1: length 0..5 (complete at 5). v2: length 0..SHOTS_PER_DAY (complete at SHOTS_PER_DAY).
  score: number;         // recomputed on every appendShot from goal count
  shareGenerated: boolean;
  // Plan #1 §4.1/§6: optional, LOCAL-ONLY fields. NEVER sent to D1
  // (ResultSubmissionPayload is unchanged). SCHEMA_VERSION stays 2;
  // migrateRecord stays a no-op. MUST be threaded through BOTH load() and
  // appendShot() or they vanish on cold reload.
  mode?: 'daily' | 'daily-hard';   // default undefined → 'daily'
  assistUsed?: boolean;            // sticky; default undefined → false
};

export function migrateRecord(raw: DailyRecord): DailyRecord {
  // No structural transformation between v1 and v2 — v1 records are 5-shot
  // complete, v2 are 7-shot. Preserving the original schemaVersion is what
  // lets isComplete and scene-routing reach the correct decisions for both
  // shapes; rewriting v1 → v2 silently extended a finished v1 round to 7 shots.
  // Future v3 migrations would add real transforms here.
  return raw;
}

export function isComplete(record: DailyRecord): boolean {
  if (record.schemaVersion === 1) return true; // v1 5-shot final
  return record.shots.length === SHOTS_PER_DAY;
}

export type { StreakState };

export type LocalState = {
  schemaVersion: 1;
  installDate: string; // YYYY-MM-DD
  history: Record<string, DailyRecord>;
  streak: StreakState;
};

export interface LocalStateStore {
  getState(): LocalState;
  getDailyRecord(date: string): DailyRecord | null;
  getStreak(): StreakState;
  getOrCreateClientId(): string;

  appendShot(date: string, shot: ShotResult): DailyRecord;
  markShared(date: string): void;
  markAssistUsed(date: string): void;
  markMode(date: string, mode: 'daily' | 'daily-hard'): void;
  applyEngagementForDate(date: string): StreakState;
  trimHistory(today: string, maxDays: number): void;
  resetAll(): void;
}

const DEFAULT_STREAK: StreakState = {
  current: 0,
  longest: 0,
  lastPlayedDate: null,
  graceUsed: false,
  daysSinceGraceReset: 0,
};

function defaultState(): LocalState {
  return {
    schemaVersion: 1,
    installDate: new Date().toISOString().slice(0, 10),
    history: {},
    streak: { ...DEFAULT_STREAK },
  };
}

function countGoals(shots: readonly ShotResult[]): number {
  return shots.filter((s) => s.outcome === 'goal').length;
}

function daysBetween(d1: string, d2: string): number {
  const a = new Date(d1).getTime();
  const b = new Date(d2).getTime();
  return Math.floor(Math.abs(b - a) / 86_400_000);
}

export function createLocalStateStore(
  storage: Storage,
  key = 'napi11es:v1',
): LocalStateStore {
  const inMemory: LocalState = load();

  function load(): LocalState {
    let raw: string | null = null;
    try {
      raw = storage.getItem(key);
    } catch (err) {
      console.warn('[local-state] storage read failed; using default', err);
      return defaultState();
    }
    if (raw === null) return defaultState();
    try {
      const parsed = JSON.parse(raw) as Partial<LocalState>;
      if (parsed.schemaVersion !== 1) return defaultState();
      // Per-record normalization: legacy records pre-Plan-5 had no schemaVersion
      // field — default them to v1. migrateRecord is currently a no-op
      // (preserves the original schemaVersion); v3+ migrations would add
      // transforms there.
      const migratedHistory: Record<string, DailyRecord> = {};
      for (const [date, rec] of Object.entries(parsed.history ?? {})) {
        const r = rec as Partial<DailyRecord> & { schemaVersion?: 1 | 2 };
        const tagged: DailyRecord = {
          shots: r.shots ?? [],
          score: r.score ?? 0,
          shareGenerated: r.shareGenerated ?? false,
          schemaVersion: r.schemaVersion ?? 1,
          ...(r.mode !== undefined && { mode: r.mode }),
          ...(r.assistUsed !== undefined && { assistUsed: r.assistUsed }),
        };
        migratedHistory[date] = migrateRecord(tagged);
      }
      // Best-effort fill of optional/typed fields:
      return {
        schemaVersion: 1, // LocalState wrapper — unchanged.
        installDate: parsed.installDate ?? defaultState().installDate,
        history: migratedHistory,
        streak: parsed.streak ?? { ...DEFAULT_STREAK },
      };
    } catch (err) {
      console.warn('[local-state] corrupted JSON; using default and overwriting on next save', err);
      return defaultState();
    }
  }

  function save(): void {
    try {
      storage.setItem(key, JSON.stringify(inMemory));
    } catch (err) {
      // Likely QuotaExceededError; try one inline trim + retry without mutating inMemory,
      // so the in-memory state remains intact even if storage is unavailable.
      // NOTE: We do NOT call trimHistory() here — that would call save() recursively.
      try {
        const today = new Date().toISOString().slice(0, 10);
        const trimmedHistory: Record<string, DailyRecord> = {};
        for (const [date, record] of Object.entries(inMemory.history)) {
          if (daysBetween(date, today) <= 30) trimmedHistory[date] = record;
        }
        const trimmedState: LocalState = { ...inMemory, history: trimmedHistory };
        storage.setItem(key, JSON.stringify(trimmedState));
      } catch (err2) {
        console.warn('[local-state] save failed; in-memory only for this session', err2 ?? err);
      }
    }
  }

  function getState(): LocalState {
    return inMemory;
  }

  function getDailyRecord(date: string): DailyRecord | null {
    return inMemory.history[date] ?? null;
  }

  function getStreak(): StreakState {
    return inMemory.streak;
  }

  function getOrCreateClientId(): string {
    return getOrCreateClientIdFromLib(storage);
  }

  function appendShot(date: string, shot: ShotResult): DailyRecord {
    const existing = inMemory.history[date] ?? { schemaVersion: SCHEMA_VERSION, shots: [], score: 0, shareGenerated: false };
    const updated: DailyRecord = {
      // Preserve the existing record's schemaVersion so a partial v1 in-progress
      // round (1–4 shots persisted by the pre-Plan-5 client) resumes as v1 and
      // is recognized as complete at 5/5 instead of being silently extended.
      schemaVersion: existing.schemaVersion,
      shots: [...existing.shots, shot],
      score: countGoals([...existing.shots, shot]),
      shareGenerated: existing.shareGenerated,
      // Plan #1 §4.1: carry the local-only fields forward (the marker + mode
      // reconstruction depend on them surviving every appendShot).
      ...(existing.mode !== undefined && { mode: existing.mode }),
      ...(existing.assistUsed !== undefined && { assistUsed: existing.assistUsed }),
    };
    inMemory.history[date] = updated;
    save();
    return updated;
  }

  function markShared(date: string): void {
    const existing = inMemory.history[date];
    if (!existing) return;
    inMemory.history[date] = { ...existing, shareGenerated: true };
    save();
  }

  function markAssistUsed(date: string): void {
    const existing = inMemory.history[date];
    if (!existing) return; // mirror markShared: no record → no-op
    // Write durably AT REVEAL TIME (not deferred to next appendShot) so the
    // reveal→close→reopen window cannot leave 🕶️ wrongly eligible (Plan #1 §4.3).
    inMemory.history[date] = { ...existing, assistUsed: true };
    save();
  }

  function markMode(date: string, mode: 'daily' | 'daily-hard'): void {
    const existing = inMemory.history[date];
    if (!existing || existing.mode === mode) return; // no record or already tagged → no-op
    inMemory.history[date] = { ...existing, mode };
    save();
  }

  function applyEngagementForDate(date: string): StreakState {
    if (inMemory.streak.lastPlayedDate === date) return inMemory.streak;
    inMemory.streak = updateStreak(inMemory.streak, date);
    save();
    return inMemory.streak;
  }

  function trimHistory(today: string, maxDays: number): void {
    const next: Record<string, DailyRecord> = {};
    for (const [date, record] of Object.entries(inMemory.history)) {
      if (daysBetween(date, today) <= maxDays) next[date] = record;
    }
    inMemory.history = next;
    save();
  }

  function resetAll(): void {
    try {
      storage.removeItem(key);
    } catch (err) {
      console.error('[local-state] resetAll removeItem threw', err);
    }
  }

  return {
    getState,
    getDailyRecord,
    getStreak,
    getOrCreateClientId,
    appendShot,
    markShared,
    markAssistUsed,
    markMode,
    applyEngagementForDate,
    trimHistory,
    resetAll,
  };
}
